import { cn } from "utils";
import { useTimesheet } from "./context";
import { Fragment, memo, useCallback } from "react";
import EntryCard from "./card/entry";
import DraftEntryCard from "./card/draft-entry";
import { ShimmerEffect } from "../shimmer";
import { ReactListItems, ReactListInitialLoader } from "@7span/react-list";

// import { ReactListItems } from "react-list";
// import { ReactListInitialLoader } from "react-list";

/**
 * @typedef {Object} TimesheetProps
 * @property {React.ReactNode} children - Child components to render within the timesheet
 * @property {string} [className] - Additional CSS classes to apply
 * @property {Object} [props] - Additional props to spread to the root element
 */

/**
 * Timesheet component that displays a container for timesheet entries and related content
 * @param {TimesheetProps} props
 * @returns {JSX.Element}
 */
export const TimesheetRoot = memo(function TimesheetRoot({
  children,
  className,
  ...props
}) {
  return (
    <ReactListItems>
      {({ items }) => {
        if (typeof children === "function") return children(items);
        return children;
      }}
    </ReactListItems>
  );
});

/**
 * @typedef {Object} TimesheetContentProps
 * @property {React.ReactNode} children - Child components to render
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [props] - Additional props to spread
 */

export const TimesheetContent = memo(function TimesheetContent({
  children,
  className,
  hideIfNoEntry,
}) {
  const {
    data: { entries },
  } = useTimesheet();
  if (hideIfNoEntry) {
    if (entries.length === 0) {
      return null;
    }
  }

  return <div className={className}>{children}</div>;
});

/**
 * @typedef {Object} TimesheetEntry
 * @property {string} id - Entry identifier
 * @property {string} title - Entry title
 * @property {string} description - Entry description
 * @property {string} duration - Entry duration
 */

/**
 * @typedef {Object} TimesheetEntriesProps
 * @property {(props: { entry: TimesheetEntry }) => React.ReactNode} [children] - Render prop for custom entry rendering
 */
/**
 * Renders a list of timesheet entries with custom or default rendering
 * Now works with ReactListItems to get individual item data
 * @param {TimesheetEntriesProps} props
 * @returns {JSX.Element|null}
 */
export const TimesheetEntries = memo(function TimesheetEntries({ children }) {
  // Handle loading, error, and empty states at the list level
  const {
    data: { entries },
  } = useTimesheet();

  if (entries.length === 0) {
    return null;
  }

  const renderEntry = (entry) => {
    return (
      <Fragment key={entry.id}>
        {children({
          entry,
        })}
      </Fragment>
    );
  };

  if (typeof children === "function") {
    return entries.map(renderEntry);
  }

  return entries.map((entry, index) => (
    <EntryCard key={entry.id} index={index} entry={entry} />
  ));
});

/**
 * @typedef {Object} DraftTimesheetEntriesProps
 * @property {(props: { entry: TimesheetEntry }) => React.ReactNode} [children] - Render prop for custom entry rendering
 */
/**
 * Renders a list of draft timesheet entries with custom or default rendering
 * Similar to TimesheetEntries but uses DraftEntryCard
 * @param {DraftTimesheetEntriesProps} props
 * @returns {JSX.Element|null}
 */
export const DraftTimesheetEntries = memo(function DraftTimesheetEntries({ children }) {
  // Handle loading, error, and empty states at the list level
  const {
    data: { entries },
  } = useTimesheet();

  if (entries.length === 0) {
    return null;
  }

  const renderEntry = (entry) => {
    return (
      <Fragment key={entry.id}>
        {children({
          entry,
        })}
      </Fragment>
    );
  };

  if (typeof children === "function") {
    return entries.map(renderEntry);
  }

  return entries.map((entry, index) => (
    <DraftEntryCard key={entry.id} index={index} entry={entry} />
  ));
});

/**
 * @typedef {Object} TimesheetLoadingProps
 * @property {number} [cardsCount=4] - Number of shimmer cards to display
 */

/**
 * Displays a loading state for timesheet entries using shimmer effects
 * @param {TimesheetLoadingProps} props
 * @returns {JSX.Element|null}
 */
export const TimesheetLoading = memo(function TimesheetLoading({
  cardsCount = 4,
  children,
}) {
  const cardCount = Math.floor(Math.random() * cardsCount) + 2;

  return <ReactListInitialLoader>{children}</ReactListInitialLoader>;
});

export function TimesheetFiltersContext() {
  const { filters, setFilters } = useTimesheet();

  return children({ filters, setFilters });
}
